"use client";

import { cookieServiceManager } from "@/lib/services/cookie-services";
import { useCookieStore } from "@/lib/stores/cookie-store";
import { texts } from "@/lib/texts";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Link from "next/link";
import { useEffect } from "react";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Label } from "./label";
import { Switch } from "./switch";

const icons = [<X key="x" className="h-4 w-4" />];

export function CookieConsentBanner() {
  const {
    consent,
    showBanner,
    showSettings,
    acceptAll,
    declineAll,
    updateConsent,
    toggleBanner,
    toggleSettings,
  } = useCookieStore();

  // Update services when consent changes
  useEffect(() => {
    cookieServiceManager.updateConsent(consent);
  }, [consent]);

  const handleAcceptAll = () => {
    acceptAll();
  };

  const handleDecline = () => {
    declineAll();
  };

  const handleAcceptSelected = () => {
    updateConsent(consent);
  };

  const handleOpenSettings = () => {
    toggleBanner(false);
    setTimeout(() => toggleSettings(true), 300);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            className="fixed right-0 bottom-4 left-0 z-50 mx-auto w-full max-w-xs rounded-lg border bg-background p-4 shadow-lg md:right-4 md:left-auto md:mx-0 md:max-w-sm"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 350,
              damping: 25,
              duration: 0.3,
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold">
                  {texts.global.cookies.banner.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {texts.global.cookies.banner.description}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 transition-opacity hover:opacity-80"
                onClick={handleDecline}
                aria-label={texts.global.cookies.banner.closeAriaLabel}
              >
                {icons[0]}
              </Button>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="w-full transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {texts.global.cookies.banner.buttons.acceptAll}
              </Button>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDecline}
                  className="flex-1 transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {texts.global.cookies.banner.buttons.decline}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOpenSettings}
                  className="flex-1 transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {texts.global.cookies.banner.buttons.settings}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showSettings} onOpenChange={toggleSettings}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{texts.global.cookies.settings.title}</DialogTitle>
            <DialogDescription>
              {texts.global.cookies.settings.description}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              {texts.global.cookies.settings.intro}
            </p>
            <div className="space-y-4">
              <div className="space-y-2 rounded-lg p-3 transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <Label htmlFor="analytics" className="font-medium">
                    {texts.global.cookies.settings.cookieTypes.analytics.title}
                  </Label>
                  <Switch
                    id="analytics"
                    checked={consent.analytics}
                    onCheckedChange={(checked) =>
                      updateConsent({ analytics: checked }, false)
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {
                    texts.global.cookies.settings.cookieTypes.analytics
                      .description
                  }
                </p>
              </div>

              <div className="space-y-2 rounded-lg p-3 transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ads" className="font-medium">
                    {texts.global.cookies.settings.cookieTypes.ads.title}
                  </Label>
                  <Switch
                    id="ads"
                    checked={consent.ads}
                    onCheckedChange={(checked) => {
                      // Update all ad-related consent together
                      updateConsent(
                        {
                          ads: checked,
                          ad_user_data: checked,
                          ad_personalization: checked,
                        },
                        false,
                      );

                      // Log for debugging
                      console.log(
                        `All ad-related consent updated to: ${checked ? "granted" : "denied"}`,
                      );
                    }}
                  />
                </div>
                <p
                  className="text-xs text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html:
                      texts.global.cookies.settings.cookieTypes.ads.description,
                  }}
                />
              </div>

              <div className="space-y-2 rounded-lg p-3 opacity-50 transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ad_user_data" className="font-medium">
                    {texts.global.cookies.settings.cookieTypes.adUserData.title}
                  </Label>
                  <Switch
                    id="ad_user_data"
                    checked={consent.ad_user_data}
                    disabled={true}
                    onCheckedChange={(checked) =>
                      updateConsent({ ad_user_data: checked }, false)
                    }
                  />
                </div>
                <p
                  className="text-xs text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html:
                      texts.global.cookies.settings.cookieTypes.adUserData
                        .description,
                  }}
                />
              </div>

              <div className="space-y-2 rounded-lg p-3 opacity-50 transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ad_personalization" className="font-medium">
                    {
                      texts.global.cookies.settings.cookieTypes
                        .adPersonalization.title
                    }
                  </Label>
                  <Switch
                    id="ad_personalization"
                    checked={consent.ad_personalization}
                    disabled={true}
                    onCheckedChange={(checked) =>
                      updateConsent({ ad_personalization: checked }, false)
                    }
                  />
                </div>
                <p
                  className="text-xs text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html:
                      texts.global.cookies.settings.cookieTypes
                        .adPersonalization.description,
                  }}
                />
              </div>

              <div className="space-y-2 rounded-lg p-3 transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <Label htmlFor="turnstile" className="font-medium">
                    {texts.global.cookies.settings.cookieTypes.turnstile.title}
                  </Label>
                  <Switch
                    id="turnstile"
                    checked={consent.turnstile}
                    onCheckedChange={(checked) =>
                      updateConsent({ turnstile: checked }, false)
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {
                    texts.global.cookies.settings.cookieTypes.turnstile
                      .description
                  }
                </p>
              </div>

              <div className="space-y-2 rounded-lg p-3 transition-colors hover:bg-muted/50">
                <div className="flex items-center justify-between">
                  <Label htmlFor="preferences" className="font-medium">
                    {
                      texts.global.cookies.settings.cookieTypes.preferences
                        .title
                    }
                  </Label>
                  <Switch
                    id="preferences"
                    checked={consent.preferences}
                    onCheckedChange={(checked) =>
                      updateConsent({ preferences: checked }, false)
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {
                    texts.global.cookies.settings.cookieTypes.preferences
                      .description
                  }
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {texts.global.cookies.settings.footer.privacyText}{" "}
              <Link
                href={texts.global.cookies.settings.footer.privacyUrl}
                className="text-primary underline hover:text-primary/80"
              >
                {texts.global.cookies.settings.footer.privacyLink}
              </Link>
              .
            </p>
          </div>

          <DialogFooter className="flex-col space-y-2 sm:space-y-0">
            <div className="flex w-full flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={handleDecline}
                className="flex-1 transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {texts.global.cookies.settings.buttons.decline}
              </Button>
              <Button
                onClick={handleAcceptSelected}
                className="flex-1 transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {texts.global.cookies.settings.buttons.saveSelection}
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="flex-1 transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {texts.global.cookies.settings.buttons.acceptAll}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
